import React, { useEffect, useState, useMemo } from 'react';
import { useALCContext, ALC_STATES } from '../context/ALCContext';
import { alcService } from '../services/alcService';
import ALCStepper from './ALCStepper';
import { useMsal } from '@azure/msal-react';
import store from '../../../../store/store';
import { addPendingSync } from '../../../../store/ALCSlice';

const ESCALATION_TIME_LIMIT = 5 * 60; // 5 minutes in seconds

const ALCPendingQA: React.FC = () => {
    const { state, dispatch, isProductionUser, isQaUser } = useALCContext();
    const { accounts } = useMsal();
    const [remainingSeconds, setRemainingSeconds] = useState(ESCALATION_TIME_LIMIT);
    const [hasEscalated, setHasEscalated] = useState(false);

    const currentUserName = accounts[0]?.name || '';
    const currentEmail = accounts[0]?.username || '';

    // Calculate requestTime and elapsed time
    const reqTimeStr = useMemo(() => {
        return state.sessionData?.requestTime || (state.sessionData as any)?.tourstartdate || (state.sessionData as any)?.createdon;
    }, [state.sessionData]);

    // Timer effect
    useEffect(() => {
        if (!reqTimeStr) return;

        const requestTime = new Date(reqTimeStr).getTime();

        const checkCountdown = () => {
            const now = new Date().getTime();
            const elapsedSeconds = Math.floor((now - requestTime) / 1000);
            const remaining = ESCALATION_TIME_LIMIT - elapsedSeconds;

            if (remaining <= 0 || isNaN(requestTime)) {
                setRemainingSeconds(0);
                if (!hasEscalated) {
                    handleEscalation();
                }
            } else {
                setRemainingSeconds(remaining);
            }
        };

        checkCountdown();
        const timer = setInterval(checkCountdown, 1000);
        return () => clearInterval(timer);
    }, [reqTimeStr, hasEscalated]);

    const handleEscalation = async () => {
        setHasEscalated(true);
        
        const escalationEmails = state.sessionData?.escalationEmails;
        if (!state.currentTourId) return;

        console.warn("Escalating ALC request. Escalation contacts:", escalationEmails);
        
        const updateData: any = {
            cr3ea_prod_qualitytourid: state.currentTourId,
            cr3ea_status: "Escalated",
            cr3ea_processstatus: "Escalated"
        };
        if (escalationEmails && escalationEmails.length > 0) {
            updateData.cr3ea_assigned_qa = escalationEmails.join(',');
        }

        if (!navigator.onLine) {
            store.dispatch(addPendingSync({
                id: `${Date.now()}_escalate_${state.currentTourId}`,
                type: 'SESSION_UPDATE',
                method: 'PATCH',
                payload: updateData,
                createdAt: Date.now()
            }));
            return;
        }

        try {
            await alcService.saveSession(updateData);
        } catch (error) {
            console.error("Failed to log escalation in Dataverse:", error);
        }
    };

    // Identity and role resolution matching state-machine.js
    const { isAssignedQA, isShiftExec, isDualRole, isGeneralQaNotAssigned, assignedQaDisplayName } = useMemo(() => {
        const assignedQaStr = (state.sessionData?.assignedQaEmail || state.sessionData?.assignedQaName || "").toLowerCase().trim();
        const shiftExecStr = (state.sessionData?.productionExecutive || "").toLowerCase().trim();

        const cleanToken = (val: string) => {
            if (!val) return "";
            let s = val.toLowerCase().trim();
            if (s.includes("\\")) s = s.split("\\").pop() || "";
            if (s.includes("|")) s = s.split("|").pop() || "";
            if (s.includes("@")) s = s.split("@")[0];
            return s.replace(/[^a-z0-9]/g, "").trim();
        };

        const myEmailClean = (currentEmail || "").toLowerCase().trim();
        const myNameClean = (currentUserName || "").toLowerCase().trim();

        const myToken = cleanToken(myEmailClean) || cleanToken(myNameClean);
        const assignedQaToken = cleanToken(assignedQaStr);
        const shiftExecToken = cleanToken(shiftExecStr);

        const isSpecificallyAssignedQA = !!(assignedQaStr && (
            assignedQaStr === myEmailClean || 
            assignedQaStr === myNameClean ||
            (myEmailClean && (assignedQaStr.includes(myEmailClean) || myEmailClean.includes(assignedQaStr))) ||
            (myNameClean && (assignedQaStr.includes(myNameClean) || myNameClean.includes(assignedQaStr))) ||
            (myToken && assignedQaToken && (myToken === assignedQaToken || assignedQaToken.includes(myToken) || myToken.includes(assignedQaToken)))
        ));

        const isEscalationMgr = hasEscalated && !!state.sessionData?.escalationEmails?.some(
            email => email.toLowerCase().trim() === myEmailClean
        );

        const assignedQA = isQaUser || isSpecificallyAssignedQA || isEscalationMgr;

        const shiftExec = isProductionUser || !shiftExecStr || !!(
            shiftExecStr === myEmailClean ||
            shiftExecStr === myNameClean ||
            (myEmailClean && (shiftExecStr.includes(myEmailClean) || myEmailClean.includes(shiftExecStr))) ||
            (myNameClean && (shiftExecStr.includes(myNameClean) || myNameClean.includes(shiftExecStr))) ||
            (myToken && shiftExecToken && (myToken === shiftExecToken || shiftExecToken.includes(myToken) || myToken.includes(shiftExecToken)))
        );

        const dual = assignedQA && shiftExec;
        const generalQaNotAssigned = isQaUser && !isSpecificallyAssignedQA && !isEscalationMgr && state.isReadOnly;

        return {
            isAssignedQA: assignedQA,
            isShiftExec: shiftExec,
            isDualRole: dual,
            isGeneralQaNotAssigned: generalQaNotAssigned,
            assignedQaDisplayName: state.sessionData?.assignedQaName || assignedQaStr || "QA Executive"
        };
    }, [state.sessionData, currentEmail, currentUserName, isQaUser, isProductionUser, hasEscalated, state.isReadOnly]);

    const handleAcceptRequest = async () => {
        if (!state.currentTourId) {
            alert("No active session ID found.");
            return;
        }

        const currentSession: any = state.sessionData || {};
        const currentStatus = (currentSession.status || currentSession.processstatus || "").trim().toLowerCase();

        let totalElapsedMinutes = 0;
        let delayMinutes = 0;
        let isPostEscalation = (currentStatus === "escalated") || hasEscalated;

        if (reqTimeStr) {
            const reqTime = new Date(reqTimeStr).getTime();
            if (!isNaN(reqTime)) {
                totalElapsedMinutes = Math.max(0, Math.floor((Date.now() - reqTime) / (60 * 1000)));
                delayMinutes = Math.max(0, totalElapsedMinutes - 5);
                if (totalElapsedMinutes >= 5) {
                    isPostEscalation = true;
                }
            }
        }

        const qaEmail = currentEmail || state.sessionData?.assignedQaEmail || "qa@bectors.com";
        const qaDisplayName = currentUserName || qaEmail;

        const auditObj = {
            tourStartTime: reqTimeStr ? new Date(reqTimeStr).toISOString() : null,
            escalationThresholdTime: reqTimeStr ? new Date(new Date(reqTimeStr).getTime() + 5 * 60 * 1000).toISOString() : null,
            acceptedTime: new Date().toISOString(),
            totalElapsedMinutes: totalElapsedMinutes,
            delayMinutes: delayMinutes,
            isPostEscalation: isPostEscalation,
            acceptedByEmail: qaEmail,
            acceptedByName: qaDisplayName,
            originalAssignedQA: currentSession.assignedQaEmail || currentSession.cr3ea_assigned_qa || "",
            shiftExecutive: currentSession.productionExecutive || currentSession.cr3ea_shiftexecutiveproduction || ""
        };

        const baseEscContacts = (currentSession.escalation_contacts || currentSession.cr3ea_escalation_contacts || "").split("||")[0].trim();
        const updatedEscContacts = baseEscContacts ? `${baseEscContacts} || AUDIT:${JSON.stringify(auditObj)}` : `AUDIT:${JSON.stringify(auditObj)}`;
        
        const newProcessStatus = isPostEscalation ? `QA In Progress (Delayed: ${delayMinutes}m)` : "QA In Progress";

        const updateData = {
            cr3ea_prod_qualitytourid: state.currentTourId,
            cr3ea_status: "QA In Progress",
            cr3ea_processstatus: newProcessStatus,
            cr3ea_tourby: qaEmail,
            cr3ea_escalation_contacts: updatedEscContacts
        };

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            if (!navigator.onLine) {
                store.dispatch(addPendingSync({
                    id: `${Date.now()}_session_${state.currentTourId}`,
                    type: 'SESSION_UPDATE',
                    method: 'PATCH',
                    payload: updateData,
                    createdAt: Date.now()
                }));
                
                alert("Request accepted offline. Transitioning to checklist.");
                dispatch({ type: 'SET_STATE', payload: ALC_STATES.QA_CHECKLIST });
                return;
            }

            await alcService.saveSession(updateData);
            dispatch({ type: 'SET_STATE', payload: ALC_STATES.QA_CHECKLIST });
        } catch (error: any) {
            alert("Failed to accept request: " + error.message);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const minutes = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    const timeDisplay = `${minutes}:${secs < 10 ? '0' : ''}${secs}`;

    return (
        <div>
            <ALCStepper />

            <div id="section-pending-qa" className="bs-card" style={{ marginBottom: '20px', padding: '30px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
                <div className="text-center" style={{ maxWidth: '750px', margin: '0 auto' }}>
                    <h4 className="mb-3 text-primary" style={{ fontWeight: 'bold', fontSize: '20px', color: '#1e3a8a' }}>
                        ALC Verification Request Raised
                    </h4>

                    {/* Escalated / Delayed State Renderings */}
                    {hasEscalated ? (
                        <>
                            {isDualRole ? (
                                <div id="qa-accept-panel">
                                    <div className="alert alert-danger" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', padding: '15px 20px', borderRadius: '6px', textAlign: 'left', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                                            <strong><i className="fa fa-clock-o"></i> ACCEPTANCE DELAYED:</strong> The initial 5-minute acceptance window has expired. Since you are the assigned QA Executive, you can accept this request to proceed with the line clearance inspection. Acceptance delay duration will be recorded in the audit trail.
                                        </div>
                                    </div>
                                    <button 
                                        type="button" 
                                        id="btn-accept-request"
                                        className="bs-btn bs-btn-success btn-lg my-2" 
                                        onClick={handleAcceptRequest}
                                        disabled={state.isLoading}
                                        style={{ backgroundColor: '#16a34a', color: '#ffffff', border: 'none', padding: '12px 28px', fontSize: '15px', fontWeight: 'bold', borderRadius: '6px', cursor: 'pointer' }}
                                    >
                                        {state.isLoading ? 'Accepting...' : 'Accept Request & Start Checklist (Delayed)'}
                                    </button>
                                </div>
                            ) : isAssignedQA ? (
                                <div id="qa-accept-panel">
                                    <div className="alert alert-danger" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', padding: '15px 20px', borderRadius: '6px', textAlign: 'left', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '14px' }}>
                                            <strong><i className="fa fa-clock-o"></i> ACCEPTANCE DELAYED:</strong> The initial 5-minute acceptance window has expired. You can accept this request now to resume the inspection. The delay duration will be recorded in the audit trail.
                                        </div>
                                    </div>
                                    <button 
                                        type="button" 
                                        id="btn-accept-request"
                                        className="bs-btn bs-btn-success btn-lg my-2" 
                                        onClick={handleAcceptRequest}
                                        disabled={state.isLoading}
                                        style={{ backgroundColor: '#16a34a', color: '#ffffff', border: 'none', padding: '12px 28px', fontSize: '15px', fontWeight: 'bold', borderRadius: '6px', cursor: 'pointer' }}
                                    >
                                        {state.isLoading ? 'Accepting...' : 'Accept Request & Start Checklist (Delayed)'}
                                    </button>
                                </div>
                            ) : isShiftExec ? (
                                <div id="production-wait-panel">
                                    <div className="alert alert-danger" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', padding: '15px 20px', borderRadius: '6px', textAlign: 'left', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '14px' }}>
                                            <strong>ACCEPTANCE DELAYED:</strong> The assigned QA Executive did not accept the request within the 5-minute limit. Awaiting QA acceptance or reassignment.
                                        </div>
                                    </div>
                                    <div className="spinner-border text-danger my-3" role="status">
                                        <span className="sr-only">Awaiting QA...</span>
                                    </div>
                                </div>
                            ) : (
                                <div id="production-wait-panel">
                                    <div className="alert alert-danger" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', padding: '15px 20px', borderRadius: '6px', textAlign: 'left', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '14px' }}>
                                            <strong>ACCEPTANCE DELAYED:</strong> The assigned QA Executive did not accept the request within the 5-minute limit. Awaiting QA acceptance.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div id="escalation-timer" style={{ marginTop: '15px' }}>
                                <span className="text-danger" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                    ⚠️ Delayed (Acceptance Window Expired)
                                </span>
                            </div>
                        </>
                    ) : (
                        /* On-Time State Renderings (within 5 minutes) */
                        <>
                            {isAssignedQA && !state.isReadOnly ? (
                                <div id="qa-accept-panel">
                                    <p className="lead text-dark" style={{ fontSize: '16px', color: '#1e293b', marginBottom: '20px' }}>
                                        Production has submitted that the line is ready for inspection clearance. Please review and accept this request to proceed with the checklist.
                                    </p>
                                    <button 
                                        type="button" 
                                        id="btn-accept-request"
                                        className="bs-btn bs-btn-success btn-lg my-2" 
                                        onClick={handleAcceptRequest}
                                        disabled={state.isLoading}
                                        style={{ backgroundColor: '#16a34a', color: '#ffffff', border: 'none', padding: '12px 28px', fontSize: '15px', fontWeight: 'bold', borderRadius: '6px', cursor: 'pointer' }}
                                    >
                                        {state.isLoading ? 'Accepting...' : 'Accept Request & Start Checklist'}
                                    </button>
                                </div>
                            ) : (
                                <div id="production-wait-panel">
                                    <p className="lead" style={{ fontSize: '16px', color: '#475569' }}>
                                        Waiting for the assigned QA Executive {assignedQaDisplayName ? <strong>{assignedQaDisplayName}</strong> : ''} to accept the inspection clearance request...
                                    </p>
                                    <div className="spinner-border text-primary my-3" role="status">
                                        <span className="sr-only">Waiting...</span>
                                    </div>

                                    {isGeneralQaNotAssigned && (
                                        <div id="qa-diagnostic-msg" className="alert alert-warning mt-3 text-start" style={{ fontSize: '14px', maxWidth: '600px', margin: '15px auto 0 auto', textAlign: 'left', border: '1px solid #ffeeba', backgroundColor: '#fff3cd', color: '#856404', padding: '15px', borderRadius: '4px' }}>
                                            <strong>Inspection Access Notice:</strong><br />
                                            You are identified as a QA User: <strong>{currentUserName}</strong> ({currentEmail || "No email"}).<br />
                                            However, this specific tour is assigned to: <strong>{assignedQaDisplayName}</strong>.<br />
                                            Only the assigned QA Executive can accept and run this clearance checklist.
                                        </div>
                                    )}
                                </div>
                            )}

                            <hr style={{ margin: '25px 0', borderColor: '#e2e8f0' }} />

                            <div id="escalation-timer" style={{ fontSize: '16px', color: '#475569' }}>
                                Time remaining for QA acceptance: <strong style={{ color: '#0f172a', fontSize: '18px' }}>{timeDisplay}</strong>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ALCPendingQA;
